import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Bot, Send, Loader2, CheckCircle2, ArrowRight, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@foundation/components/ui/button';
import { Textarea } from '@foundation/components/ui/textarea';
import { useBoostPolicyQA } from '../../hooks/useBoostPolicyQA';
import type { QASessionPair } from '../../hooks/useBoostPolicyQA';
import type { OrganizationProfileData, QAMessage } from '../../types/boostPolicy';

type PolicyContextQAStepProps = {
  orgId: string;
  orgProfile: OrganizationProfileData | null;
  documentTitle: string;
  policyName: string;
  relevantContext: string[];
  documentTags: string[];
  /** Called when the user continues to Review — passes full memory, session pairs, and any additional free-text context */
  onComplete: (memoryContent: string, sessionPairs: QASessionPair[], additionalContext: string) => void;
};

const AiBubble = ({ message }: { message: QAMessage }) => (
  <div className="flex items-start gap-3 max-w-[85%]">
    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-[#33b864]/10 border border-[#33b864]/20 flex items-center justify-center mt-0.5">
      <Bot className="h-4 w-4 text-[#33b864]" />
    </div>
    <div className="rounded-2xl rounded-tl-sm bg-muted/60 border border-border px-4 py-3 text-sm text-foreground leading-relaxed [&_p]:my-0 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0">
      {message.text ? (
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>
      ) : (
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <span className="inline-flex gap-0.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[#33b864] animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="h-1.5 w-1.5 rounded-full bg-[#33b864] animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="h-1.5 w-1.5 rounded-full bg-[#33b864] animate-bounce" style={{ animationDelay: '300ms' }} />
          </span>
          Thinking…
        </span>
      )}
    </div>
  </div>
);

const UserBubble = ({ message }: { message: QAMessage }) => (
  <div className="flex justify-end">
    <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-[#33b864] px-4 py-3 text-sm text-white leading-relaxed">
      {message.text}
    </div>
  </div>
);

export const PolicyContextQAStep = ({
  orgId,
  orgProfile,
  documentTitle,
  policyName,
  relevantContext,
  documentTags,
  onComplete,
}: PolicyContextQAStepProps) => {
  const [inputValue, setInputValue] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { messages, status, isContextComplete, memoryContent, sessionPairs, submitAnswer, error } =
    useBoostPolicyQA({ orgId, orgProfile, documentTitle, policyName, relevantContext, documentTags });

  const answeredCount = messages.filter((m) => m.role === 'user').length;

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when waiting for user
  useEffect(() => {
    if (status === 'waiting') {
      inputRef.current?.focus();
    }
  }, [status]);

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || status === 'streaming' || status === 'loading') return;
    setInputValue('');
    await submitAnswer(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
        <Loader2 className="h-7 w-7 animate-spin text-[#33b864]" />
        <p className="text-sm">Loading your context history…</p>
      </div>
    );
  }

  // Error state (fatal — no messages loaded)
  if (status === 'error' && messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
          <AlertCircle className="h-6 w-6 text-destructive" />
        </div>
        <div className="text-center space-y-1">
          <p className="text-sm font-medium text-foreground">Something went wrong</p>
          <p className="text-xs text-muted-foreground">{error ?? 'Could not start the Q&A session.'}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => window.location.reload()}
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Reload
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Info banner */}
      <div className="rounded-lg border border-border bg-muted/60 px-4 py-3 text-sm text-foreground flex items-start gap-2">
        <Bot className="h-4 w-4 text-[#33b864] mt-0.5 flex-shrink-0" />
        <span>
          <span className="font-medium">AI Policy Assistant</span> — Answer a few questions to give the AI context for generating a more accurate, tailored policy. Your answers are saved and won't be asked again next time.
        </span>
      </div>

      {/* Chat window */}
      <div
        ref={scrollRef}
        className="flex flex-col gap-4 min-h-[320px] max-h-[420px] overflow-y-auto rounded-xl border border-border bg-background p-4"
      >
        {messages.map((msg, idx) =>
          msg.role === 'ai' ? (
            <AiBubble key={idx} message={msg} />
          ) : (
            <UserBubble key={idx} message={msg} />
          ),
        )}

        {/* Inline error (stream failed mid-conversation) */}
        {status === 'error' && messages.length > 0 && (
          <div className="flex items-center gap-2 text-xs text-destructive px-2">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
            {error ?? 'Something went wrong. Please try again.'}
          </div>
        )}
      </div>

      {/* Input area */}
      {!isContextComplete && (
        <div className="flex gap-2 items-end">
          <Textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your answer… (Enter to send, Shift+Enter for new line)"
            className="flex-1 min-h-[60px] max-h-[120px] resize-none text-sm"
            disabled={status === 'streaming' || status === 'loading'}
            rows={2}
          />
          <Button
            onClick={() => void handleSend()}
            disabled={!inputValue.trim() || status === 'streaming' || status === 'loading'}
            className="h-10 w-10 p-0 bg-[#33b864] hover:bg-[#2da358] text-white flex-shrink-0"
          >
            {status === 'streaming' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      )}

      {/* Additional context — always visible so user can add notes at any point */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          Additional context <span className="normal-case font-normal">(optional)</span>
        </label>
        <Textarea
          value={additionalContext}
          onChange={(e) => setAdditionalContext(e.target.value)}
          placeholder="Anything else the AI should know? e.g. specific hazards, existing processes, internal requirements, current pain points…"
          className="min-h-[80px] max-h-[160px] resize-none text-sm"
          rows={3}
        />
      </div>

      {/* Context complete — show continue prompt */}
      {isContextComplete && (
        <div className="rounded-xl border border-[#33b864]/30 bg-[#33b864]/5 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <CheckCircle2 className="h-5 w-5 text-[#33b864] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Great — I have enough context!</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Your answers have been saved. The AI will use them to generate a more relevant, specific policy.
              </p>
            </div>
          </div>
          <Button
            onClick={() => onComplete(memoryContent, sessionPairs, additionalContext)}
            className="gap-2 bg-[#33b864] hover:bg-[#2da358] text-white flex-shrink-0"
          >
            Continue to Review
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Skip & continue — visible until all asked questions are complete */}
      {!isContextComplete && (
        <div className="rounded-xl border border-amber-300/40 bg-amber-50/60 dark:bg-amber-500/5 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-start gap-2.5 flex-1">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              Answering these questions helps the AI generate a more accurate, tailored policy.
              If you skip, the policy will be generated with less context and may be less specific.
              {answeredCount > 0 && (
                <>
                  {' '}
                  <span className="font-medium text-foreground">
                    {answeredCount} answer{answeredCount !== 1 ? 's' : ''} recorded so far.
                  </span>
                </>
              )}
            </p>
          </div>
          <Button
            onClick={() => onComplete(memoryContent, sessionPairs, additionalContext)}
            disabled={status === 'streaming'}
            className="gap-2 flex-shrink-0 bg-[#33b864] hover:bg-[#2da358] text-white"
          >
            Skip &amp; Continue to Review
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
